import { CartLinesUpdateEvent } from '@shopify/events';

// The one-second transition presents mounting guidance, not an external lookup.
class CarplayCompatibilityChecker extends HTMLElement {
  connectedCallback() {
    this.controller?.abort();
    this.controller = new AbortController();
    const { signal } = this.controller;
    this.purchaseBlock = this.closest('.buy-buttons-block--carplay');
    this.modelInput = this.querySelector('[data-motorcycle]');
    this.resultShell = this.querySelector('[data-result-shell]');
    this.verifyButton = this.querySelector('[data-verify]');
    this.adapterButton = this.querySelector('[data-add-adapter]');
    this.adapterAvailable = !this.adapterButton.disabled;
    this.querySelector('[data-fit-form]').addEventListener('submit', this.verify, { signal });
    this.modelInput.addEventListener('input', this.resetResult, { signal });
    this.addEventListener('click', this.onClick, { signal });
  }

  disconnectedCallback() {
    this.controller.abort();
    clearTimeout(this.analysisTimer);
  }

  resetResult = () => {
    clearTimeout(this.analysisTimer);
    this.setChecking(false);
    this.modelInput.setCustomValidity('');
    this.resultShell.hidden = true;
    this.removeAttribute('data-has-result');
  };

  setChecking(checking) {
    this.checking = checking;
    this.verifyButton.disabled = checking;
    this.modelInput.readOnly = checking;
    this.querySelectorAll('[data-mount]').forEach((chip) => { chip.disabled = checking; });
    this.querySelector('[data-verify-label]').style.visibility = checking ? 'hidden' : '';
    this.querySelector('[data-loading]').hidden = !checking;
    this.querySelector('[data-analysis-status]').textContent = checking ? 'Analyse de votre configuration…' : '';
    this.resultShell.setAttribute('aria-busy', String(checking));
  }

  verify = (event) => {
    event.preventDefault();
    if (this.checking) return;
    if (!this.modelInput.value.trim()) {
      this.modelInput.setCustomValidity('Indiquez la marque, le modèle et l’année de votre moto.');
      this.modelInput.reportValidity();
      return;
    }
    const mount = this.querySelector('[data-mount][aria-pressed="true"]').dataset.mount;
    this.setChecking(true);
    this.querySelector('[data-result-title]').textContent = mount === 'quad-lock'
      ? 'Réutilisez votre support actuel'
      : 'Installation possible avec le support universel fourni';
    const description = this.querySelector('[data-result-description]');
    if (mount === 'quad-lock') {
      description.replaceChildren(this.querySelector('[data-quad-description]').content.cloneNode(true));
    } else {
      description.textContent = mount === 'other'
        ? 'La compatibilité avec votre support GPS/téléphone actuel doit être vérifiée.'
        : 'Votre moto doit simplement disposer d’un emplacement adapté sur le guidon.';
    }
    this.adapterButton.hidden = mount !== 'quad-lock';
    this.querySelector('[data-adapter-status]').hidden = mount !== 'quad-lock' || !this.querySelector('[data-adapter-status]').textContent;
    // The invisible final result reserves exactly its own height during the animation.
    this.setAttribute('data-has-result', '');
    this.resultShell.hidden = false;
    this.analysisTimer = setTimeout(() => {
      this.setChecking(false);
    }, 1000);
  };

  async addAdapter() {
    if (this.adding || this.adapterAdded || !this.adapterAvailable || !this.dataset.adapterId) return;
    this.adding = true;
    this.adapterButton.disabled = true;
    const label = this.adapterButton.textContent;
    this.adapterButton.textContent = 'Ajout…';
    const status = this.querySelector('[data-adapter-status]');
    status.hidden = true;
    const deferred = CartLinesUpdateEvent.createPromise();
    deferred.promise.catch(() => {});
    this.dispatchEvent(new CartLinesUpdateEvent({
      action: 'add',
      context: 'product',
      lines: [{ merchandiseId: this.dataset.adapterId, quantity: 1 }],
      promise: deferred.promise,
    }));
    let added = false;
    try {
      const sections = [...new Set(Array.from(document.querySelectorAll('cart-items-component'))
        .map((item) => item.dataset.sectionId).filter(Boolean))].slice(0, 5);
      const response = await fetch(this.dataset.cartAddUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: [{ id: Number(this.dataset.adapterId), quantity: 1 }],
          sections,
          sections_url: window.location.pathname,
        }),
      });
      const data = await response.json();
      if (!response.ok || data.status) throw new Error(data.description || 'Ajout impossible. Réessayez dans un instant.');
      added = true;
      this.adapterAdded = true;
      this.adapterButton.textContent = 'Ajouté ✓';
      status.textContent = 'Adaptateur ajouté au panier.';
      status.hidden = false;
      // A direct purchase replaces a pending bundle selection, avoiding an accidental second adapter.
      const bundle = this.purchaseBlock?.querySelector('[data-cart-addon-variant-id]');
      if (bundle?.checked) {
        bundle.checked = false;
        bundle.dispatchEvent(new Event('change', { bubbles: true }));
      }
      const cartResponse = await fetch(this.dataset.cartUrl);
      if (!cartResponse.ok) throw new Error('Cart refresh failed');
      const cart = await cartResponse.json();
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: { items: cart.items, sections: data.sections, source: 'carplay-mount-checker', didError: false },
      });
    } catch (error) {
      deferred.reject(error);
      if (added) {
        status.textContent = 'Adaptateur ajouté. Actualisez le panier pour le voir.';
      } else {
        status.textContent = error.message || 'Ajout impossible. Réessayez dans un instant.';
        this.adapterButton.textContent = label;
        this.adapterButton.disabled = false;
      }
      status.hidden = false;
    } finally {
      this.adding = false;
    }
  }

  onClick = async (event) => {
    const chip = event.target.closest('[data-mount]');
    if (chip && !this.checking) {
      this.querySelectorAll('[data-mount]').forEach((button) => {
        button.setAttribute('aria-pressed', String(button === chip));
      });
      this.resetResult();
    }
    if (event.target.closest('[data-add-adapter]')) await this.addAdapter();
    if (event.target.closest('[data-fit-contact]')) {
      await customElements.whenDefined('carplay-purchase-help');
      if (!this.isConnected) return;
      const help = this.purchaseBlock?.querySelector('carplay-purchase-help');
      if (!help) return;
      help.openPopup(true);
      help.querySelector('[data-reason="Compatibilité avec ma moto"]')?.click();
    }
  };
}

if (!customElements.get('carplay-compatibility-checker')) {
  customElements.define('carplay-compatibility-checker', CarplayCompatibilityChecker);
}
